import { getCouponCode } from '../helpers';
import { createInstantSubscriptionCart } from './ace';
import { HEADERS, simpleFetch, simpleFetchWithHttp } from './customFetch';
import { getSubscriptionProductUuid, resolveIsCartPath } from './uncurl';

// Build the Cart 2.0 / UnCurl upgrade body: create an INSTANT cart for the upgrade-TO product and
// send its uuid. `current_product_uuid` is the product being upgraded FROM (null when the user has
// no resolvable box subscription — e.g. no existing sub or a club member). Throws (caught by the
// caller, which falls back to legacy) if the target product isn't configured or the cart fails.
const buildUpgradeCartBody = async (plan, currentPlan, couponCode) => {
  const targetProductUuid = getSubscriptionProductUuid(plan);
  if (!targetProductUuid) {
    throw new Error(
      `Missing subscription product UUID for plan "${plan}"; cannot build upgrade cart.`,
    );
  }

  const cartResponse = await createInstantSubscriptionCart(targetProductUuid);
  const cartUuid = cartResponse?.data?.items?.[0]?.uuid;
  if (cartResponse?.error || !cartUuid) {
    throw new Error('Failed to create INSTANT cart for upgrade.');
  }

  return {
    cart_uuid: cartUuid,
    current_product_uuid: getSubscriptionProductUuid(currentPlan) || null,
    ...(couponCode && { coupon_code: couponCode }),
  };
};

/**
 * Upgrade the current subscription. Dual-path, gated by the `uncurl-partial-rollout` flag: when the
 * flag is on it sends a Cart 2.0 body (`cart_uuid` + `current_product_uuid`); otherwise the legacy
 * Recurly body (`target_plan_code` + `current_plan_code`). Pass `forceLegacyPath: true` to always
 * take the legacy path (trials, which are out of the Cart 2.0 scope, do this). Any failure building
 * the cart body falls back to legacy so the upgrade never breaks. Resolves to the raw fetch Response.
 * @param plan
 * @param currentPlan
 * @param couponsApplied
 * @param captchaToken
 * @param root0
 * @param root0.forceLegacyPath
 */
export const updateSubscription = async (
  plan,
  currentPlan,
  couponsApplied = [],
  captchaToken,
  { forceLegacyPath = false } = {},
) => {
  const couponCode = getCouponCode(couponsApplied);
  const headers = {
    ...HEADERS,
    'hcaptcha-token': captchaToken,
  };

  const postUpgrade = (body) =>
    simpleFetchWithHttp(
      '/api/2/upgrade',
      { method: 'POST', body: JSON.stringify(body), headers },
      process.env.REACT_APP_REGISTRATION_API,
    );

  const isCartPath = !forceLegacyPath && (await resolveIsCartPath());
  if (isCartPath) {
    try {
      return postUpgrade(await buildUpgradeCartBody(plan, currentPlan, couponCode));
    } catch (error) {
      console.error(
        'Cart-path upgrade failed to build; falling back to legacy plan-code path.',
        error,
      );
    }
  }

  return postUpgrade({
    target_plan_code: plan,
    current_plan_code: currentPlan,
    coupon_code: couponCode,
  });
};

export const reactivateExpiredSubscription = (plan, promoCode, captchaToken, promoCodeType) => {
  const headers = {
    ...HEADERS,
    'hcaptcha-token': captchaToken,
  };

  const payload = {
    plan_code: plan,
    ...(promoCodeType === 'coupon' && { coupon_code: promoCode }),
    ...(promoCodeType === 'giftcard' && { gift_card: promoCode }),
  };

  return simpleFetchWithHttp(
    '/api/2/reactivate/expired',
    {
      method: 'POST',
      body: JSON.stringify(payload),
      headers,
    },
    process.env.REACT_APP_REGISTRATION_API,
  );
};

export const reactivateCancelledSubscription = () => {
  return simpleFetchWithHttp(
    '/api/1/reactivate/cancelled',
    {
      method: 'POST',
      body: JSON.stringify({
        coupon_code: '',
      }),
    },
    process.env.REACT_APP_REGISTRATION_API,
  );
};

export const getPriceLock = (status) => {
  const url = status ? `/api/1/price/lock?status=${status}` : '/api/1/price/lock';
  return simpleFetch(
    url,
    {
      method: 'GET',
    },
    process.env.REACT_APP_GROWTH_API,
  );
};

export const postPriceLock = () =>
  simpleFetchWithHttp(
    '/api/1/price/lock',
    {
      method: 'POST',
    },
    process.env.REACT_APP_GROWTH_API,
  );
